<template>
  <li class="flex flex-row items-center gap-3 py-2.5 text-gray-700">
    <a
      class="p-2 -m-2 flex gap-2 items-center justify-between cursor-pointer transition hover:text-blue-500"
      :href="`/courses/${course.id}/assignments/${assignment.id}/marking/${user.id}/tasks/first`"
      target="_blank"
    >
      <s-icon name="open-in-new" size="16" />
      <span class="sr-only">
        {{
          t('actions.openInNewTab', {
            assignmentName: assignment.name,
          })
        }}
      </span>
    </a>
    <a
      class="w-full group flex gap-2 items-center justify-between cursor-pointer transition hover:text-blue-500"
      :href="`/courses/${course.id}/assignments/${assignment.id}/marking/${user.id}/tasks/first`"
    >
      <span
        class="underline underline-offset-2 decoration-transparent group-hover:decoration-blue-200/70 group-active:decoration-blue-200 decoration-2 group-hover:decoration-blue-200 transition-all ease-out duration-150"
      >
        {{ assignment.name }}
      </span>

      <span
        class="px-2 py-1 flex-shrink-0 rounded-full border shadow text-sm font-bold leading-none"
        :class="gradePillColorClassMap[gradeToColor(assignment.grade)]"
      >
        <s-grade
          class="h-fit"
          :grade-display="course.gradeDisplay"
          :grade="assignment.grade"
          tooltip
        ></s-grade>
      </span>
    </a>
    <s-btn
      v-if="assignment.extension && can.grantExtensions"
      :href="
        route('courses.assignments.extension.edit', {
          course: course.id,
          assignment: assignment.id,
          extension: assignment.extension.id,
          _query: {
            // Encode Current url
            redirect: encodeURIComponent(getCurrentPath()),
          },
        })
      "
      icon="calendar-edit"
      size="sm"
      color="blue-light"
      :title="t('actions.editExtension')"
    >
      {{ t('actions.editExtension') }}
    </s-btn>
    <s-btn
      v-else-if="can.grantExtensions"
      :href="
        route('courses.assignments.extension.create', {
          course: course.id,
          assignment: assignment.id,
          _query: {
            redirect: getCurrentPath(),
            user: user.id,
          },
        })
      "
      icon="calendar-plus"
      size="sm"
      color="white"
      :title="t('actions.grantExtension')"
    >
      {{ t('actions.grantExtension') }}
    </s-btn>
  </li>
</template>
<script setup lang="ts">
import {route} from 'ziggy-js';
import SBtn from '../../../design-system/SBtn.vue';
import SIcon from '../../../design-system/SIcon.vue';
import SGrade from '../../../components/SGrade.vue';
import {useI18n} from 'vue-i18n';
import {User} from '../../../types/entities/user.js';
import {Course} from '../../../types/entities/course.js';
import StudentCourseAssignmentRowDto = App.DTOs.CourseAssignments.StudentCourseAssignmentRowDto;
import AssignmentGradeDto = App.DTOs.AssignmentGradeDto;
import ShowParticipantDto = App.DTOs.CourseParticipant.ShowParticipantDto;

defineProps<{
  course: Course;
  assignment: StudentCourseAssignmentRowDto;
  user: ShowParticipantDto;
  can: {
    grantExtensions: boolean;
  };
}>();

const {t} = useI18n();

const getCurrentPath = () => {
  return window.location.pathname;
};

const gradePillColorClassMap = {
  green: 'border-[#C1EF9B] bg-[#D4FFB1] text-[#689A3F]',
  blue: 'border-blue-100 bg-blue-50 text-blue-800',
  red: 'border-red-200 bg-red-100 text-red-800',
  gray: 'border-gray-300 bg-gray-100 text-gray-800',
};

const gradeToColor = (grade: AssignmentGradeDto | null) => {
  if (!grade) {
    return 'gray';
  }

  const numericGrade = +((grade.exactNumerator / grade.exactDenominator) * 100).toFixed(0);

  if (numericGrade >= 85) {
    return 'green';
  }

  if (numericGrade <= 50) {
    return 'red';
  }

  return 'blue';
};
</script>
<i18n>
{
  "en": {
    "actions.openInNewTab": "Open {assignmentName} in a new tab",
    "actions.editExtension": "Edit Extension",
    "actions.grantExtension": "Grant Extension"
  },
  "fr": {
    "actions.openInNewTab": "Ouvrir {assignmentName} dans un nouvel onglet",
    "actions.editExtension": "Modifier la prolongation",
    "actions.grantExtension": "Accorder une prologation"
  }
}
</i18n>
